import { useGetRouteMetadataQuery } from "../../services/routing";
import { useRiskViewerState } from "../../features/riskViewer/riskViewerSlice";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { resetSurface, setRouteTime, setSelectedTime } from "../../features/surface/surfaceSlice";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { formatRouteForecastTime } from "./RiskViewerUtils";

/**
 * Custom hook to fetch route metadata based on the selected route.
 * It retrieves route details and updates the redux store based on the returned route metadata.
 *
 * @returns {Object} - An object containing:
 *   - routeMetadata: The metadata of the selected route if available, otherwise undefined.
 *   - isFetchingRouteData: Boolean indicating if route metadata is currently being fetched.
 */
export const useRouteMetadata = () =>
{
    const { selectedRoute } = useRiskViewerState();
    const dispatch = useDispatch();
    const routeId = selectedRoute?.hexString;

    const { data: routeMetadataRaw } = useGetRouteMetadataQuery( routeId ?? skipToken, {
        skip: routeId === undefined,
    } );

    const routeMetadata = routeId === undefined ? undefined : routeMetadataRaw;

    useEffect( () =>
    {
        //whenever the routeMetadata is updated, set the selected time to the start time of the route and the forecast time
        if ( routeMetadata?.startTime !== undefined )
        {
            dispatch( setSelectedTime( routeMetadata?.startTime.valueOf() ) );
        }
        //whenever the routeMetadata is updated, update the forecast time of the window.
        if ( routeMetadata !== undefined && routeMetadata?.forecastTime !== undefined )
        {
            // Use route's forecast time IF route selected/displayed
            const routeForecastTimeFT = formatRouteForecastTime( routeMetadata.forecastTime );
            dispatch( setRouteTime( routeForecastTimeFT ) );
        }
        else if ( routeMetadata === undefined )
        {
            dispatch( resetSurface( { forecastTime: undefined, tauTimeout: false } ) );
        }
    },
    [ routeMetadata ] );

    return { routeMetadata: routeMetadata };
};
